import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { motion, useAnimation, useMotionValue, useTransform } from 'framer-motion';
import { getCityBoundary, geoToSVG, getBoundaryCenter, isPointInBoundary } from '../data/accurateSanDiegoBoundaries';
import './Home.css';
import SanDiegoMailDeliveryMap from './SanDiegoMailDeliveryMap';
import CalendarWithTemplates from './CalendarWithTemplates';
import IndustrySelector from './IndustrySelector';
import logo from '../assets/sandiegomailer.webp';

const Home = () => {
  const navigate = useNavigate();
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [houses, setHouses] = useState([]);
  const [currentTruckPosition, setCurrentTruckPosition] = useState({ x: 50, y: 300 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [userCity, setUserCity] = useState('San Diego County');
  const [locationLoading, setLocationLoading] = useState(true);
  const [cityBoundary, setCityBoundary] = useState(null);
  
  // Advanced truck state management
  const [truckState, setTruckState] = useState('IDLE'); // IDLE, ROUTE_PLANNING, DRIVING, APPROACHING, PARKING, DELIVERY, RETURNING
  const [currentRoute, setCurrentRoute] = useState([]);
  const [truckVelocity, setTruckVelocity] = useState({ x: 0, y: 0 });
  const [truckDirection, setTruckDirection] = useState(0); // Angle in degrees
  const [deliveryQueue, setDeliveryQueue] = useState([]);
  const [routeProgress, setRouteProgress] = useState(0);
  const [truckStats, setTruckStats] = useState({
    totalDistance: 0,
    fuelUsed: 0,
    deliveriesCompleted: 0,
    efficiency: 100
  });
  
  // Mail carrier character system
  const [carrierState, setCarrierState] = useState('IDLE'); // IDLE, WALKING, DELIVERING, RETURNING_TO_TRUCK
  const [carrierPosition, setCarrierPosition] = useState({ x: 0, y: 0 });
  const [carrierDirection, setCarrierDirection] = useState(0);
  const [carrierVisible, setCarrierVisible] = useState(false);
  const [mailBagWeight, setMailBagWeight] = useState(100); // Percentage of mail remaining
  const [walkingRoute, setWalkingRoute] = useState([]);
  const [currentWalkTarget, setCurrentWalkTarget] = useState(null);
  
  // Package delivery system
  const [packageTruckActive, setPackageTruckActive] = useState(false);
  const [packageTruckPosition, setPackageTruckPosition] = useState({ x: 100, y: 350 });
  const [packageQueue, setPackageQueue] = useState([]);
  const [dollyActive, setDollyActive] = useState(false);
  
  const animationIntervalRef = useRef(null);
  const aerialViewRef = useRef(null);
  const mailTruckRef = useRef(null);
  const physicsIntervalRef = useRef(null);
  const stateTimeoutRef = useRef(null);
  const mailCarrierRef = useRef(null);
  const packageTruckRef = useRef(null);
  const dollyRef = useRef(null);

  // Framer Motion animation controls
  const truckControls = useAnimation();
  const carrierControls = useAnimation();
  const packageTruckControls = useAnimation();
  const dollyControls = useAnimation();

  // Motion values for smooth position tracking
  const truckX = useMotionValue(currentTruckPosition.x);
  const truckY = useMotionValue(currentTruckPosition.y);
  const carrierX = useMotionValue(carrierPosition.x);
  const carrierY = useMotionValue(carrierPosition.y);
  const packageTruckX = useMotionValue(packageTruckPosition.x);
  const packageTruckY = useMotionValue(packageTruckPosition.y);
  const dollyX = useMotionValue(100);
  const dollyY = useMotionValue(360);
  
  // Animation state variables
  const [isCarrierWalking, setIsCarrierWalking] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('medical');

  const totalHouses = 45; // Reduced for better visibility

  // Physics system for realistic truck movement
  const updateTruckPhysics = (targetX, targetY) => {
    const currentX = truckX.get();
    const currentY = truckY.get();
    const dx = targetX - currentX;
    const dy = targetY - currentY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate if truck is still moving (within threshold)
    const isMoving = distance > 5;
    const velocityX = isMoving ? (dx / distance) * 2 : 0;
    const velocityY = isMoving ? (dy / distance) * 2 : 0;
    
    return {
      moving: isMoving,
      velocityX,
      velocityY,
      distance
    };
  };

  // Advanced Route Planning System
  const calculateOptimalRoute = (startPos, targetHouses) => {
    if (targetHouses.length === 0) return [];
    
    // Create waypoints following street grid
    const waypoints = [];
    const streetGrid = {
      horizontal: [40, 100, 160, 220, 280, 340, 400],
      vertical: [60, 140, 220, 300, 380, 460, 540, 620, 700, 780, 860, 940, 1020]
    };
    
    // Find nearest street intersection to start
    const nearestStartX = streetGrid.vertical.reduce((prev, curr) => 
      Math.abs(curr - startPos.x) < Math.abs(prev - startPos.x) ? curr : prev
    );
    const nearestStartY = streetGrid.horizontal.reduce((prev, curr) => 
      Math.abs(curr - startPos.y) < Math.abs(prev - startPos.y) ? curr : prev
    );
    
    waypoints.push({ x: nearestStartX, y: nearestStartY, type: 'start' });
    
    // Group houses by proximity and create efficient route
    const sortedHouses = [...targetHouses].sort((a, b) => {
      const distA = Math.sqrt(Math.pow(a.x - startPos.x, 2) + Math.pow(a.y - startPos.y, 2));
      const distB = Math.sqrt(Math.pow(b.x - startPos.x, 2) + Math.pow(b.y - startPos.y, 2));
      return distA - distB;
    });
    
    // Create route through neighborhoods
    sortedHouses.forEach((house, index) => {
      const nearestX = streetGrid.vertical.reduce((prev, curr) => 
        Math.abs(curr - house.x) < Math.abs(prev - house.x) ? curr : prev
      );
      const nearestY = streetGrid.horizontal.reduce((prev, curr) => 
        Math.abs(curr - house.y) < Math.abs(prev - house.y) ? curr : prev
      );
      
      // Add intersection waypoint
      waypoints.push({ 
        x: nearestX, 
        y: nearestY, 
        type: 'intersection',
        houses: [house],
        deliveryZone: true
      });
    });
    
    return waypoints;
  };

  // Framer Motion physics-based movement system
  const animateTruckToPosition = async (targetX, targetY, options = {}) => {
    const {
      duration = 2,
      type = "spring",
      stiffness = 100,
      damping = 25,
      mass = 1.5
    } = options;

    // Calculate rotation angle for realistic turning
    const dx = targetX - truckX.get();
    const dy = targetY - truckY.get();
    const targetAngle = Math.atan2(dy, dx) * 180 / Math.PI;
    
    setTruckDirection(targetAngle);
    
    // Update position state
    setCurrentTruckPosition({ x: targetX, y: targetY });

    // Animate with spring physics
    await truckControls.start({
      x: targetX,
      y: targetY,
      rotate: targetAngle,
      transition: {
        type,
        stiffness,
        damping,
        mass,
        duration: type === "tween" ? duration : undefined
      }
    });

    // Update motion values
    truckX.set(targetX);
    truckY.set(targetY);
  };

  // Enhanced mail carrier walking with smooth motion
  const animateCarrierWalking = async (targetX, targetY, options = {}) => {
    const {
      duration = 1.5,
      walkingBounce = true
    } = options;

    // Calculate walking direction
    const dx = targetX - carrierX.get();
    const dy = targetY - carrierY.get();
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
    
    setCarrierDirection(angle);
    setCarrierPosition({ x: targetX, y: targetY });

    // Animate walking with bounce effect
    await carrierControls.start({
      x: targetX,
      y: targetY,
      rotate: angle,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 15,
        mass: 0.8
      }
    });

    // Update motion values
    carrierX.set(targetX);
    carrierY.set(targetY);
  };

  // Enhanced Motion-based mail delivery with realistic physics
  const createMotionBasedMailDelivery = (targetHouse) => {
    // Create mail piece element with Motion controls
    const mail = document.createElement('div');
    mail.className = 'mail-piece motion-enhanced';
    
    // Position at truck initially
    const startX = currentTruckPosition.x + 18;
    const startY = currentTruckPosition.y + 10;
    mail.style.left = startX + 'px';
    mail.style.top = startY + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(mail);
      
      // Calculate trajectory with slight arc for realism
      const targetX = targetHouse.x;
      const targetY = targetHouse.y;
      const distance = Math.sqrt(Math.pow(targetX - startX, 2) + Math.pow(targetY - startY, 2));
      
      // Add slight upward arc based on distance
      const arcHeight = Math.min(distance * 0.3, 50);
      const midX = (startX + targetX) / 2;
      const midY = (startY + targetY) / 2 - arcHeight;
      
      // Animate with custom spring physics
      const motionMail = mail.animate([
        { 
          left: startX + 'px', 
          top: startY + 'px',
          transform: 'scale(1) rotate(0deg)',
          opacity: 1
        },
        { 
          left: midX + 'px', 
          top: midY + 'px',
          transform: 'scale(1.1) rotate(180deg)',
          opacity: 0.9
        },
        { 
          left: targetX + 'px', 
          top: targetY + 'px',
          transform: 'scale(0.8) rotate(360deg)',
          opacity: 1
        }
      ], {
        duration: Math.max(800, distance * 1.5),
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Smooth realistic easing
        fill: 'forwards'
      });
      
      // Create enhanced delivery burst with Motion
      motionMail.onfinish = () => {
        createMotionDeliveryBurst(targetX, targetY);
        
        // Mark house as delivered with spring animation
        if (targetHouse.element) {
          targetHouse.element.animate([
            { transform: 'scale(1)', filter: 'brightness(1)' },
            { transform: 'scale(1.05)', filter: 'brightness(1.2)' },
            { transform: 'scale(1)', filter: 'brightness(1.1)' }
          ], {
            duration: 600,
            easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', // Spring bounce
            fill: 'forwards'
          });
          
          targetHouse.element.classList.add('delivered');
        }
        
        // Remove mail piece after delivery
        setTimeout(() => mail.remove(), 500);
      };
    }
  };

  // Enhanced delivery burst with realistic particle physics
  const createMotionDeliveryBurst = (x, y) => {
    const burst = document.createElement('div');
    burst.className = 'delivery-burst motion-enhanced';
    burst.style.left = (x - 25) + 'px';
    burst.style.top = (y - 25) + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(burst);
      
      // Create multiple particles for realistic burst effect
      for (let i = 0; i < 6; i++) {
        const particle = document.createElement('div');
        particle.className = 'burst-particle';
        const angle = (i / 6) * 2 * Math.PI;
        const distance = 15 + Math.random() * 10;
        const particleX = Math.cos(angle) * distance;
        const particleY = Math.sin(angle) * distance;
        
        burst.appendChild(particle);
        
        // Animate particles with spring physics
        particle.animate([
          { 
            transform: 'translate(0, 0) scale(0)', 
            opacity: 1 
          },
          { 
            transform: `translate(${particleX}px, ${particleY}px) scale(1.2)`, 
            opacity: 0.8 
          },
          { 
            transform: `translate(${particleX * 1.5}px, ${particleY * 1.5}px) scale(0)`, 
            opacity: 0 
          }
        ], {
          duration: 1200 + Math.random() * 400,
          easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fill: 'forwards'
        });
      }
      
      // Remove burst after animation
      setTimeout(() => burst.remove(), 2000);
    }
  };

  // Detect user location and get city name
  const detectUserLocation = () => {
    setLocationLoading(true);
    
    if (!navigator.geolocation) {
      console.log('Geolocation not supported, using default San Diego location');
      const defaultBoundary = getCityBoundary('san-diego-county');
      setCityBoundary(defaultBoundary);
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        
        try {
          // Use reverse geocoding to get city name
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          const data = await response.json();
          
          const city = data.city || data.locality || data.principalSubdivision || 'San Diego';
          const area = data.localityInfo?.administrative?.[3]?.name || '';
          
          // Create descriptive location string
          let locationString = city;
          if (area && area !== city) {
            locationString = `${area}, ${city}`;
          }
          
          // Check if location is in San Diego County
          const isInSanDiego = city.toLowerCase().includes('san diego') || 
                              data.principalSubdivision?.toLowerCase().includes('san diego') ||
                              (latitude >= 32.5 && latitude <= 33.6 && longitude >= -117.7 && longitude <= -116.1);
          
          if (isInSanDiego) {
            setUserCity(locationString);
            // Get more specific boundary if possible
            const boundaryData = getCityBoundary(locationString);
            setCityBoundary(boundaryData);
            console.log('San Diego location detected:', locationString);
          } else {
            setUserCity('San Diego County');
            const defaultBoundary = getCityBoundary('san-diego-county');
            setCityBoundary(defaultBoundary);
            console.log('Non-San Diego location, using county boundary');
          }
          
        } catch (error) {
          console.error('Error getting city name:', error);
          setUserCity('San Diego County');
          const defaultBoundary = getCityBoundary('san-diego-county');
          setCityBoundary(defaultBoundary);
        }
        
        setLocationLoading(false);
      },
      (error) => {
        console.log('Location access denied or failed, using default:', error.message);
        setUserCity('San Diego County');
        const defaultBoundary = getCityBoundary('san-diego-county');
        setCityBoundary(defaultBoundary);
        setLocationLoading(false);
      },
      {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000 // 5 minutes cache
      }
    );
  };

  // Enhanced point-in-boundary check using the accurate boundary utility
  const isPointInBoundaryCheck = (x, y) => {
    if (!cityBoundary || !cityBoundary.features || !cityBoundary.features[0]) {
      // Fallback to simple boundary check
      const containerWidth = aerialViewRef.current?.offsetWidth || 1200;
      const containerHeight = aerialViewRef.current?.offsetHeight || 450;
      const svgX = (x / containerWidth) * 1200;
      const svgY = (y / containerHeight) * 450;
      const centerX = 600, centerY = 250, radiusX = 350, radiusY = 150;
      const dx = (svgX - centerX) / radiusX;
      const dy = (svgY - centerY) / radiusY;
      return (dx * dx + dy * dy) <= 1;
    }
    
    const containerWidth = aerialViewRef.current?.offsetWidth || 1200;
    const containerHeight = aerialViewRef.current?.offsetHeight || 450;
    
    // Convert screen coordinates to geographic coordinates
    // First normalize to viewport
    const normalizedX = x / containerWidth;
    const normalizedY = y / containerHeight;
    
    // Then convert to geographic bounds (San Diego County)
    const bounds = {
      minLng: -117.604064,
      maxLng: -116.118790,
      minLat: 32.531756,
      maxLat: 33.505729
    };
    
    const lng = bounds.minLng + normalizedX * (bounds.maxLng - bounds.minLng);
    const lat = bounds.maxLat - normalizedY * (bounds.maxLat - bounds.minLat); // Flip Y
    
    // Use the utility function from our accurate boundaries
    return isPointInBoundary(lat, lng, cityBoundary);
  };

  // Create San Diego-style street grid with realistic patterns
  const createStreetGrid = () => {
    const streetGrid = document.getElementById('street-grid');
    if (!streetGrid) return;
    
    streetGrid.innerHTML = '';
    
    // Create major arterials (wider streets)
    const majorStreets = [
      { type: 'horizontal', position: 180, name: 'Mission Valley' },
      { type: 'horizontal', position: 280, name: 'University Ave' },
      { type: 'vertical', position: 240, name: 'I-5 Corridor' },
      { type: 'vertical', position: 400, name: 'I-15 Corridor' }
    ];
    
    majorStreets.forEach(street => {
      const element = document.createElement('div');
      element.className = `street street-${street.type} major-arterial`;
      element.style[street.type === 'horizontal' ? 'top' : 'left'] = street.position + 'px';
      element.setAttribute('data-name', street.name);
      streetGrid.appendChild(element);
    });
    
    // Regular streets
    for (let i = 0; i < 8; i++) {
      if (i * 60 + 40 !== 180 && i * 60 + 40 !== 280) { // Skip major arterials
        const street = document.createElement('div');
        street.className = 'street street-horizontal';
        street.style.top = (i * 60 + 40) + 'px';
        streetGrid.appendChild(street);
      }
    }
    
    for (let i = 0; i < 15; i++) {
      if (i * 80 + 60 !== 240 && i * 80 + 60 !== 400) { // Skip major arterials
        const street = document.createElement('div');
        street.className = 'street street-vertical';
        street.style.left = (i * 80 + 60) + 'px';
        streetGrid.appendChild(street);
      }
    }
  };

  // Enhanced neighborhood creation based on San Diego geography
  const createNeighborhood = () => {
    const aerialView = aerialViewRef.current;
    if (!aerialView) return;
    
    const newHouses = [];
    
    // Remove existing houses
    const existingHouses = aerialView.querySelectorAll('.house');
    existingHouses.forEach(house => house.remove());
    
    // Get area dimensions
    const containerWidth = aerialView.offsetWidth || 1200;
    const containerHeight = aerialView.offsetHeight || 450;
    
    // San Diego neighborhood patterns - better spaced and fewer clusters
    const neighborhoods = [
      { name: 'Coastal', centerX: 0.18, centerY: 0.55, density: 1.0, radius: 0.25 }, // La Jolla/PB area
      { name: 'Downtown', centerX: 0.22, centerY: 0.75, density: 1.2, radius: 0.15 }, // Downtown core
      { name: 'North County', centerX: 0.22, centerY: 0.25, density: 0.8, radius: 0.3 }, // Oceanside/Carlsbad
      { name: 'East County', centerX: 0.75, centerY: 0.45, density: 0.6, radius: 0.35 }, // El Cajon area
    ];
    
    neighborhoods.forEach((neighborhood, clusterIndex) => {
      const clusterCenterX = neighborhood.centerX * containerWidth;
      const clusterCenterY = neighborhood.centerY * containerHeight;
      const maxRadius = neighborhood.radius * Math.min(containerWidth, containerHeight);
      const housesInCluster = Math.floor((totalHouses / neighborhoods.length) * neighborhood.density);
      
      for (let i = 0; i < housesInCluster && newHouses.length < totalHouses; i++) {
        let finalX, finalY;
        let attempts = 0;
        const maxAttempts = 25;
        
        do {
          // Create more spread out distribution
          const streetBias = Math.random() < 0.5; // 50% chance to align with streets
          
          if (streetBias) {
            // Align with street grid but more spread out
            const streetX = Math.floor(clusterCenterX / 120) * 120 + (Math.random() - 0.5) * 100;
            const streetY = Math.floor(clusterCenterY / 80) * 80 + (Math.random() - 0.5) * 60;
            finalX = streetX + (Math.random() - 0.5) * maxRadius * 0.8;
            finalY = streetY + (Math.random() - 0.5) * maxRadius * 0.6;
          } else {
            // Random distribution within cluster - more spread out
            const angle = Math.random() * Math.PI * 2;
            const distance = (0.3 + Math.random() * 0.7) * maxRadius; // Minimum 30% distance from center
            finalX = clusterCenterX + Math.cos(angle) * distance;
            finalY = clusterCenterY + Math.sin(angle) * distance * 0.8;
          }
          
          // Add some hill/canyon variation for realism
          if (neighborhood.name === 'East County') {
            finalY += Math.sin(finalX * 0.008) * 25; // Hills
          }
          
          // Ensure minimum distance from other houses
          let tooClose = false;
          const minDistance = 35; // Minimum pixels between houses
          for (let existingHouse of newHouses) {
            const dx = finalX - existingHouse.x;
            const dy = finalY - existingHouse.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < minDistance) {
              tooClose = true;
              break;
            }
          }
          
          if (tooClose) {
            attempts++;
            continue;
          }
          
          // Ensure bounds
          finalX = Math.max(30, Math.min(finalX, containerWidth - 30));
          finalY = Math.max(30, Math.min(finalY, containerHeight - 30));
          
          attempts++;
        } while (!isPointInBoundaryCheck(finalX, finalY) && attempts < maxAttempts);
        
        // If still can't place in boundary, use a safe fallback position
        if (attempts >= maxAttempts) {
          finalX = clusterCenterX + (Math.random() - 0.5) * maxRadius * 0.5;
          finalY = clusterCenterY + (Math.random() - 0.5) * maxRadius * 0.3;
          finalX = Math.max(25, Math.min(finalX, containerWidth - 25));
          finalY = Math.max(25, Math.min(finalY, containerHeight - 25));
        }
        
        const houseElement = document.createElement('div');
        houseElement.className = 'house';
        houseElement.style.left = finalX + 'px';
        houseElement.style.top = finalY + 'px';
        houseElement.setAttribute('data-house-id', newHouses.length);
        houseElement.setAttribute('data-neighborhood', neighborhood.name);
        houseElement.setAttribute('data-cluster', clusterIndex);
        
        // Add some visual variety based on neighborhood
        if (neighborhood.name === 'Coastal') {
          houseElement.classList.add('coastal-style');
        } else if (neighborhood.name === 'Downtown') {
          houseElement.classList.add('urban-style');
        }
        
        aerialView.appendChild(houseElement);
        newHouses.push({
          element: houseElement,
          delivered: false,
          id: newHouses.length,
          x: finalX,
          y: finalY,
          cluster: clusterIndex,
          neighborhood: neighborhood.name
        });
      }
    });
    
    setHouses(newHouses);
  };

  // Advanced Truck State Machine
  const updateTruckState = () => {
    switch (truckState) {
      case 'IDLE':
        handleIdleState();
        break;
      case 'ROUTE_PLANNING':
        handleRoutePlanningState();
        break;
      case 'DRIVING':
        handleDrivingState();
        break;
      case 'APPROACHING':
        handleApproachingState();
        break;
      case 'PARKING':
        handleParkingState();
        break;
      case 'DELIVERY':
        handleDeliveryState();
        break;
      case 'RETURNING':
        handleReturningState();
        break;
      default:
        setTruckState('IDLE');
    }
  };

  const handleIdleState = () => {
    const undeliveredHouses = houses.filter(h => !h.delivered);
    if (undeliveredHouses.length > 0) {
      setTruckState('ROUTE_PLANNING');
      
      // Add startup animation
      const mailTruck = mailTruckRef.current;
      if (mailTruck) {
        mailTruck.classList.add('startup');
        setTimeout(() => mailTruck.classList.remove('startup'), 1000);
      }
    }
  };

  const handleRoutePlanningState = () => {
    const undeliveredHouses = houses.filter(h => !h.delivered);
    if (undeliveredHouses.length === 0) {
      setTruckState('IDLE');
      return;
    }

    // Check if package delivery should activate instead
    if (activatePackageDelivery()) {
      // Package truck is handling deliveries
      setTruckState('IDLE');
      return;
    }

    // Select houses for this delivery run (2-4 houses)
    const deliveryCount = Math.min(Math.floor(Math.random() * 3) + 2, undeliveredHouses.length);
    const selectedHouses = undeliveredHouses.slice(0, deliveryCount);
    
    // Calculate optimal route
    const route = calculateOptimalRoute(currentTruckPosition, selectedHouses);
    setCurrentRoute(route);
    setDeliveryQueue(selectedHouses);
    setRouteProgress(0);
    
    // Transition to driving after planning delay
    setTimeout(() => {
      if (route.length > 0) {
        setTruckState('DRIVING');
      }
    }, 1500); // Planning delay
  };

  const handleDrivingState = async () => {
    if (currentRoute.length === 0) {
      setTruckState('IDLE');
      return;
    }

    const currentWaypoint = currentRoute[routeProgress];
    if (!currentWaypoint) {
      setTruckState('APPROACHING');
      return;
    }

    try {
      // Animate truck to waypoint with realistic physics
      await animateTruckToPosition(currentWaypoint.x, currentWaypoint.y, {
        type: "spring",
        stiffness: 80,
        damping: 20,
        mass: 2.0 // Heavier feel for truck
      });

      // Add driving animation class during movement
      const mailTruck = mailTruckRef.current;
      if (mailTruck) {
        mailTruck.classList.add('driving');
      }

      // Check if reached final waypoint
      if (currentWaypoint.deliveryZone) {
        setTruckState('APPROACHING');
      } else {
        // Move to next waypoint
        setRouteProgress(prev => prev + 1);
      }

      // Remove driving animation
      setTimeout(() => {
        if (mailTruck) {
          mailTruck.classList.remove('driving');
        }
      }, 500);

    } catch (error) {
      console.log('Animation interrupted:', error);
      setTruckState('IDLE');
    }
  };

  const handleApproachingState = () => {
    // Slow down and prepare for parking
    const mailTruck = mailTruckRef.current;
    if (mailTruck) {
      mailTruck.classList.remove('driving');
      mailTruck.classList.add('approaching');
    }
    
    setTimeout(() => {
      setTruckState('PARKING');
    }, 800);
  };

  const handleParkingState = async () => {
    // Find optimal parking position near delivery houses
    if (deliveryQueue.length > 0) {
      const avgX = deliveryQueue.reduce((sum, h) => sum + h.x, 0) / deliveryQueue.length;
      const avgY = deliveryQueue.reduce((sum, h) => sum + h.y, 0) / deliveryQueue.length;
      
      // Park slightly offset from houses
      const parkingX = avgX - 40;
      const parkingY = avgY + 15;
      
      const mailTruck = mailTruckRef.current;
      if (mailTruck) {
        mailTruck.classList.add('parking');
      }

      try {
        // Animate parking with gentle motion
        await animateTruckToPosition(parkingX, parkingY, {
          type: "spring",
          stiffness: 60,
          damping: 30,
          mass: 2.5 // Even heavier for parking maneuvers
        });

        // Brief pause for parking completion
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setTruckState('DELIVERY');

        if (mailTruck) {
          mailTruck.classList.remove('parking');
        }

      } catch (error) {
        console.log('Parking animation interrupted:', error);
        setTruckState('DELIVERY');
      }
    }
  };

  const handleDeliveryState = () => {
    if (deliveryQueue.length === 0) {
      setTruckState('RETURNING');
      return;
    }

    // Initialize mail carrier position relative to truck
    initializeMailCarrier();

    // Decide delivery method based on house clustering
    if (shouldUseMailCarrier(deliveryQueue)) {
      // Use mail carrier for clustered houses
      startMailCarrierDelivery(deliveryQueue);
      
      // Mark all houses as delivered after carrier completes
      const totalCarrierTime = deliveryQueue.length * 2000 + 3000; // Walking + delivery time
      setTimeout(() => {
        deliveryQueue.forEach(house => {
          // Update truck stats
          setTruckStats(prev => ({
            ...prev,
            deliveriesCompleted: prev.deliveriesCompleted + 1,
            efficiency: Math.max(87, prev.efficiency - Math.random() * 1.5) // Better efficiency with carrier
          }));
        });
        
        setDeliveryQueue([]);
        setTruckState('RETURNING');
      }, totalCarrierTime);
      
    } else {
      // Use traditional truck delivery for spread out houses
      deliveryQueue.forEach((house, index) => {
        setTimeout(() => {
          createMotionBasedMailDelivery(house);
          house.delivered = true;
          setDeliveredCount(prev => prev + 1);
          
          // Update truck stats
          setTruckStats(prev => ({
            ...prev,
            deliveriesCompleted: prev.deliveriesCompleted + 1,
            efficiency: Math.max(85, prev.efficiency - Math.random() * 2)
          }));
        }, index * 1200); // Staggered deliveries
      });

      // Clear delivery queue and transition
      setTimeout(() => {
        setDeliveryQueue([]);
        setTruckState('RETURNING');
      }, deliveryQueue.length * 1200 + 1000);
    }
  };

  const handleReturningState = () => {
    // Return to depot or continue route
    const undeliveredHouses = houses.filter(h => !h.delivered);
    
    if (undeliveredHouses.length === 0) {
      // All deliveries complete - return to depot
      const depotX = 50;
      const depotY = 300;
      
      const physics = updateTruckPhysics(depotX, depotY);
      setCurrentTruckPosition(prev => ({
        x: prev.x + truckVelocity.x,
        y: prev.y + truckVelocity.y
      }));

      const mailTruck = mailTruckRef.current;
      if (mailTruck) {
        mailTruck.style.left = (currentTruckPosition.x + truckVelocity.x) + 'px';
        mailTruck.style.top = (currentTruckPosition.y + truckVelocity.y) + 'px';
        mailTruck.classList.add('returning');
      }

      if (!physics.moving) {
        // Reset for next cycle
        setTimeout(() => {
          resetDeliveryAnimation();
        }, 3000);
      }
    } else {
      // More deliveries to make
      setTruckState('ROUTE_PLANNING');
    }
  };

  const resetDeliveryAnimation = () => {
    // Reset all houses and start over
    setHouses(prevHouses => 
      prevHouses.map(h => {
        h.element.classList.remove('delivered');
        h.delivered = false;
        return h;
      })
    );
    setDeliveredCount(0);
    setTruckState('IDLE');
    setRouteProgress(0);
    setCurrentRoute([]);
    setDeliveryQueue([]);
    
    // Reset truck position
    const containerHeight = aerialViewRef.current?.offsetHeight || 400;
    const isMobile = window.innerWidth <= 768;
    const newPosition = { 
      x: isMobile ? 30 : 50, 
      y: Math.min(isMobile ? 250 : 300, containerHeight - 50)
    };
    setCurrentTruckPosition(newPosition);
    setTruckVelocity({ x: 0, y: 0 });
    setTruckDirection(0);

    const mailTruck = mailTruckRef.current;
    if (mailTruck) {
      mailTruck.style.left = newPosition.x + 'px';
      mailTruck.style.top = newPosition.y + 'px';
      mailTruck.style.transform = 'rotate(0deg)';
      mailTruck.className = 'mail-truck'; // Reset all classes
    }
  };

  // Initialize mail truck
  const initMailTruck = () => {
    const mailTruck = mailTruckRef.current;
    if (!mailTruck) return;
    
    mailTruck.style.left = currentTruckPosition.x + 'px';
    mailTruck.style.top = currentTruckPosition.y + 'px';
    mailTruck.style.transform = 'rotate(0deg)';
  };

  // Move truck to area near target houses with smooth animation
  const moveTruckToArea = (targetHouses) => {
    if (targetHouses.length === 0) return;
    
    const avgX = targetHouses.reduce((sum, h) => sum + h.x, 0) / targetHouses.length;
    const avgY = targetHouses.reduce((sum, h) => sum + h.y, 0) / targetHouses.length;
    
    // Simulate following streets with more realistic pathfinding
    const nearestStreetX = Math.floor(avgX / 80) * 80;
    const nearestStreetY = Math.floor(avgY / 60) * 60;
    
    const newX = nearestStreetX - 35 + (Math.random() - 0.5) * 30;
    const newY = nearestStreetY + 25 + (Math.random() - 0.5) * 15;
    
    const containerWidth = aerialViewRef.current?.offsetWidth || 1200;
    const containerHeight = aerialViewRef.current?.offsetHeight || 450;
    const boundedX = Math.max(20, Math.min(newX, containerWidth - 40));
    const boundedY = Math.max(20, Math.min(newY, containerHeight - 30));
    
    // Calculate distance for animation timing
    const distance = Math.sqrt(
      Math.pow(boundedX - currentTruckPosition.x, 2) + 
      Math.pow(boundedY - currentTruckPosition.y, 2)
    );
    
    const animationDuration = Math.min(Math.max(distance * 3, 800), 2000); // 0.8s to 2s based on distance
    
    setCurrentTruckPosition({ x: boundedX, y: boundedY });
    
    const mailTruck = mailTruckRef.current;
    if (mailTruck) {
      // Add CSS transition for smooth movement
      mailTruck.style.transition = `all ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
      mailTruck.style.left = boundedX + 'px';
      mailTruck.style.top = boundedY + 'px';
      
      // Add driving animation class
      mailTruck.classList.add('driving');
      
      setTimeout(() => {
        mailTruck.classList.remove('driving');
        mailTruck.style.transition = ''; // Reset transition
      }, animationDuration);
    }
  };

  // Advanced mail delivery system with multiple stages
  const createAdvancedMailDelivery = (targetHouse) => {
    // Stage 1: Mail sorting in truck (brief pause)
    const mailTruck = mailTruckRef.current;
    if (mailTruck) {
      mailTruck.classList.add('sorting');
      setTimeout(() => mailTruck.classList.remove('sorting'), 800);
    }

    setTimeout(() => {
      // Stage 2: Create mail piece with enhanced animation
      const mail = document.createElement('div');
      mail.className = 'mail-piece advanced';
      
      // Start from truck position with slight offset
      mail.style.left = currentTruckPosition.x + 18 + 'px';
      mail.style.top = currentTruckPosition.y + 10 + 'px';
      
      // Calculate target position with more precision
      const targetX = targetHouse.x - currentTruckPosition.x - 5;
      const targetY = targetHouse.y - currentTruckPosition.y - 5;
      
      mail.style.setProperty('--target-x', targetX + 'px');
      mail.style.setProperty('--target-y', targetY + 'px');
      
      // Add mail type for visual variety
      const mailTypes = ['letter', 'package', 'priority'];
      const randomType = mailTypes[Math.floor(Math.random() * mailTypes.length)];
      mail.classList.add(randomType);
      
      const aerialView = aerialViewRef.current;
      if (aerialView) {
        aerialView.appendChild(mail);
      }
      
      setTimeout(() => {
        mail.classList.add('flying');
        
        // Stage 3: Enhanced delivery effects
        setTimeout(() => {
          createEnhancedDeliveryBurst(targetHouse.x + 12, targetHouse.y + 12);
          
          // Stage 4: House response animation
          targetHouse.element.classList.add('delivered');
          targetHouse.element.classList.add('receiving-mail');
          
          // Create house activity (lights, door, etc.)
          setTimeout(() => {
            targetHouse.element.classList.remove('receiving-mail');
            createHouseActivity(targetHouse);
          }, 1000);
          
          mail.remove();
        }, 2200); // Longer flight time for more realistic delivery
      }, 300);
    }, 800); // Wait for sorting animation
  };

  // Enhanced delivery burst with multiple effects
  const createEnhancedDeliveryBurst = (x, y) => {
    // Main delivery burst
    const burst = document.createElement('div');
    burst.className = 'delivery-burst enhanced';
    burst.style.left = (x - 20) + 'px';
    burst.style.top = (y - 20) + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(burst);
    }
    
    setTimeout(() => {
      burst.classList.add('active');
      
      // Add sparkle effects
      for (let i = 0; i < 5; i++) {
        setTimeout(() => {
          createSparkleEffect(x, y, i);
        }, i * 100);
      }
      
      setTimeout(() => burst.remove(), 1500);
    }, 50);
  };

  // Sparkle effect for delivery confirmation
  const createSparkleEffect = (x, y, index) => {
    const sparkle = document.createElement('div');
    sparkle.className = 'delivery-sparkle';
    
    const offsetX = (Math.random() - 0.5) * 40;
    const offsetY = (Math.random() - 0.5) * 40;
    
    sparkle.style.left = (x + offsetX) + 'px';
    sparkle.style.top = (y + offsetY) + 'px';
    sparkle.style.animationDelay = (index * 0.1) + 's';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(sparkle);
      setTimeout(() => sparkle.remove(), 800);
    }
  };

  // House activity animation (lights, movement)
  const createHouseActivity = (house) => {
    const element = house.element;
    
    // Window light effect
    element.classList.add('window-activity');
    
    // Create satisfaction indicator
    const satisfaction = document.createElement('div');
    satisfaction.className = 'satisfaction-indicator';
    satisfaction.innerHTML = '✓';
    satisfaction.style.left = house.x + 25 + 'px';
    satisfaction.style.top = house.y - 15 + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(satisfaction);
      setTimeout(() => {
        satisfaction.classList.add('show');
        setTimeout(() => {
          satisfaction.remove();
          element.classList.remove('window-activity');
        }, 2000);
      }, 200);
    }
  };

  // Legacy function for compatibility
  const createMailDelivery = (targetHouse) => {
    createAdvancedMailDelivery(targetHouse);
  };

  // Mail Carrier Character System
  const initializeMailCarrier = () => {
    const mailCarrier = mailCarrierRef.current;
    if (!mailCarrier) return;
    
    // Position carrier near truck initially
    const initialX = currentTruckPosition.x - 15;
    const initialY = currentTruckPosition.y + 25;
    
    setCarrierPosition({ x: initialX, y: initialY });
    mailCarrier.style.left = initialX + 'px';
    mailCarrier.style.top = initialY + 'px';
    mailCarrier.style.display = 'none'; // Hidden initially
  };

  const shouldUseMailCarrier = (houses) => {
    // Use mail carrier for clusters of 3+ houses within 60px of each other
    if (houses.length < 3) return false;
    
    const avgX = houses.reduce((sum, h) => sum + h.x, 0) / houses.length;
    const avgY = houses.reduce((sum, h) => sum + h.y, 0) / houses.length;
    
    // Check if all houses are within walking distance
    return houses.every(house => {
      const distance = Math.sqrt(
        Math.pow(house.x - avgX, 2) + Math.pow(house.y - avgY, 2)
      );
      return distance <= 80; // Walking distance threshold
    });
  };

  const createWalkingRoute = (startPos, targetHouses) => {
    const route = [{ x: startPos.x, y: startPos.y, type: 'start' }];
    
    // Sort houses by proximity for efficient walking route
    const sortedHouses = [...targetHouses].sort((a, b) => {
      const distA = Math.sqrt(Math.pow(a.x - startPos.x, 2) + Math.pow(a.y - startPos.y, 2));
      const distB = Math.sqrt(Math.pow(b.x - startPos.x, 2) + Math.pow(b.y - startPos.y, 2));
      return distA - distB;
    });
    
    // Add each house as waypoint
    sortedHouses.forEach(house => {
      route.push({
        x: house.x - 5, // Stop slightly before house
        y: house.y + 8,
        type: 'delivery',
        house: house
      });
    });
    
    // Return to truck
    route.push({ x: startPos.x, y: startPos.y, type: 'return' });
    
    return route;
  };

  const startMailCarrierDelivery = (targetHouses) => {
    if (!shouldUseMailCarrier(targetHouses)) {
      // Fallback to regular truck delivery
      targetHouses.forEach((house, index) => {
        setTimeout(() => {
          createMotionBasedMailDelivery(house);
          house.delivered = true;
          setDeliveredCount(prev => prev + 1);
        }, index * 1200);
      });
      return;
    }

    // Initialize carrier for walking delivery
    const mailCarrier = mailCarrierRef.current;
    if (!mailCarrier) return;

    // Show carrier and start walking sequence
    setCarrierVisible(true);
    mailCarrier.style.display = 'block';
    setCarrierState('WALKING');
    
    // Create walking route
    const route = createWalkingRoute(carrierPosition, targetHouses);
    setWalkingRoute(route);
    setCurrentWalkTarget(0);
    
    // Start walking animation
    walkToNextTarget(route, 0);
  };

  const walkToNextTarget = async (route, targetIndex) => {
    if (targetIndex >= route.length) {
      // Finished route - hide carrier with smooth fade
      setCarrierState('IDLE');
      
      try {
        await carrierControls.start({
          opacity: 0,
          scale: 0.8,
          transition: { duration: 0.5 }
        });
        
        setCarrierVisible(false);
        const mailCarrier = mailCarrierRef.current;
        if (mailCarrier) {
          mailCarrier.style.display = 'none';
        }
      } catch (error) {
        console.log('Carrier fade animation interrupted:', error);
      }
      
      return;
    }

    const target = route[targetIndex];
    const deliveryDelay = 1500; // time to spend at each house
    
    try {
      // Animate walking to target with smooth motion
      setCarrierState('WALKING');
      await animateCarrierWalking(target.x, target.y);
      
      if (target.type === 'delivery') {
        // Deliver to house with animation
        setCarrierState('DELIVERING');
        
        // Add delivery animation
        await carrierControls.start({
          scale: [1, 1.1, 1],
          transition: { duration: 0.5, times: [0, 0.5, 1] }
        });
        
        setTimeout(() => {
          createMailCarrierDelivery(target.house);
          target.house.delivered = true;
          setDeliveredCount(prev => prev + 1);
          
          // Reduce mail bag weight with animation
          setMailBagWeight(prev => Math.max(0, prev - (100 / deliveryQueue.length)));
          
          // Continue to next target
          setCarrierState('WALKING');
          walkToNextTarget(route, targetIndex + 1);
        }, deliveryDelay);
      } else {
        // Continue walking
        walkToNextTarget(route, targetIndex + 1);
      }
    } catch (error) {
      console.log('Walking animation interrupted:', error);
      // Continue with next target
      walkToNextTarget(route, targetIndex + 1);
    }
  };

  const animateWalking = (start, end, speed, onComplete) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance < 3) {
      setCarrierPosition({ x: end.x, y: end.y });
      onComplete();
      return;
    }
    
    const stepX = (dx / distance) * speed;
    const stepY = (dy / distance) * speed;
    
    // Calculate direction for sprite orientation
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
    setCarrierDirection(angle);
    
    const walkInterval = setInterval(() => {
      setCarrierPosition(prev => {
        const newX = prev.x + stepX;
        const newY = prev.y + stepY;
        
        // Update visual position
        const mailCarrier = mailCarrierRef.current;
        if (mailCarrier) {
          mailCarrier.style.left = newX + 'px';
          mailCarrier.style.top = newY + 'px';
          mailCarrier.style.transform = `rotate(${angle}deg)`;
          mailCarrier.classList.add('walking');
        }
        
        // Check if reached target
        const remainingDistance = Math.sqrt(
          Math.pow(end.x - newX, 2) + Math.pow(end.y - newY, 2)
        );
        
        if (remainingDistance < 3) {
          clearInterval(walkInterval);
          
          // Remove walking animation
          if (mailCarrier) {
            mailCarrier.classList.remove('walking');
          }
          
          onComplete();
          return { x: end.x, y: end.y };
        }
        
        return { x: newX, y: newY };
      });
    }, 16); // 60 FPS
  };

  const createMailCarrierDelivery = (targetHouse) => {
    // Enhanced mail delivery from carrier
    const mail = document.createElement('div');
    mail.className = 'mail-piece carrier-delivery';
    
    // Start from carrier position
    mail.style.left = carrierPosition.x + 8 + 'px';
    mail.style.top = carrierPosition.y + 5 + 'px';
    
    // Short trajectory to house mailbox
    const targetX = targetHouse.x - carrierPosition.x + 12;
    const targetY = targetHouse.y - carrierPosition.y + 8;
    
    mail.style.setProperty('--target-x', targetX + 'px');
    mail.style.setProperty('--target-y', targetY + 'px');
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(mail);
    }
    
    setTimeout(() => {
      mail.classList.add('flying');
      
      setTimeout(() => {
        createEnhancedDeliveryBurst(targetHouse.x + 12, targetHouse.y + 12);
        targetHouse.element.classList.add('delivered');
        targetHouse.element.classList.add('carrier-delivered');
        
        setTimeout(() => {
          targetHouse.element.classList.remove('receiving-mail');
          createHouseActivity(targetHouse);
        }, 800);
        
        mail.remove();
      }, 1000); // Shorter flight time for carrier delivery
    }, 200);
  };

  // Package Delivery System
  const initializePackageTruck = () => {
    const packageTruck = packageTruckRef.current;
    if (!packageTruck) return;
    
    packageTruck.style.left = packageTruckPosition.x + 'px';
    packageTruck.style.top = packageTruckPosition.y + 'px';
    packageTruck.style.display = 'none'; // Hidden initially
  };

  const shouldUsePackageTruck = () => {
    // Randomly activate package truck (20% chance per delivery cycle)
    return Math.random() < 0.2 && !packageTruckActive;
  };

  const activatePackageDelivery = () => {
    if (!shouldUsePackageTruck()) return false;
    
    setPackageTruckActive(true);
    const packageTruck = packageTruckRef.current;
    if (packageTruck) {
      packageTruck.style.display = 'block';
      packageTruck.classList.add('arriving');
    }

    // Select 1-2 houses for package delivery
    const undeliveredHouses = houses.filter(h => !h.delivered);
    const packageHouses = undeliveredHouses.slice(0, Math.min(2, undeliveredHouses.length));
    setPackageQueue(packageHouses);

    // Start package delivery sequence
    setTimeout(() => {
      startPackageDeliverySequence(packageHouses);
    }, 2000);

    return true;
  };

  const startPackageDeliverySequence = (targetHouses) => {
    if (targetHouses.length === 0) {
      deactivatePackageTruck();
      return;
    }

    // Move package truck to delivery area
    const avgX = targetHouses.reduce((sum, h) => sum + h.x, 0) / targetHouses.length;
    const avgY = targetHouses.reduce((sum, h) => sum + h.y, 0) / targetHouses.length;
    
    const parkingX = avgX - 50;
    const parkingY = avgY + 20;
    
    animatePackageTruck(packageTruckPosition, { x: parkingX, y: parkingY }, () => {
      // Start package deliveries
      targetHouses.forEach((house, index) => {
        setTimeout(() => {
          deliverPackageToHouse(house);
        }, index * 3000); // Longer delay for packages
      });

      // Deactivate truck after all deliveries
      setTimeout(() => {
        deactivatePackageTruck();
      }, targetHouses.length * 3000 + 2000);
    });
  };

  const animatePackageTruck = (start, end, onComplete) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const duration = Math.max(distance * 4, 2000); // Slower than mail truck
    
    const packageTruck = packageTruckRef.current;
    if (packageTruck) {
      packageTruck.style.transition = `all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
      packageTruck.style.left = end.x + 'px';
      packageTruck.style.top = end.y + 'px';
      
      packageTruck.classList.add('driving');
      
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
      packageTruck.style.transform = `rotate(${angle}deg)`;
    }
    
    setPackageTruckPosition({ x: end.x, y: end.y });
    
    setTimeout(() => {
      if (packageTruck) {
        packageTruck.classList.remove('driving');
        packageTruck.style.transition = '';
      }
      onComplete();
    }, duration);
  };

  const deliverPackageToHouse = (targetHouse) => {
    // Activate dolly for heavy packages (50% chance)
    const usesDolly = Math.random() < 0.5;
    
    if (usesDolly) {
      setDollyActive(true);
      
      // Show dolly animation
      const dolly = dollyRef.current;
      if (dolly) {
        dolly.style.display = 'block';
        dolly.style.left = (packageTruckPosition.x + 20) + 'px';
        dolly.style.top = (packageTruckPosition.y + 10) + 'px';
        dolly.classList.add('active');
      }
    }

    // Create package
    const packageElement = document.createElement('div');
    packageElement.className = 'package-item large';
    if (usesDolly) packageElement.classList.add('heavy');
    
    packageElement.style.left = packageTruckPosition.x + 15 + 'px';
    packageElement.style.top = packageTruckPosition.y + 8 + 'px';
    
    const targetX = targetHouse.x - packageTruckPosition.x + 15;
    const targetY = targetHouse.y - packageTruckPosition.y + 12;
    
    packageElement.style.setProperty('--target-x', targetX + 'px');
    packageElement.style.setProperty('--target-y', targetY + 'px');
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(packageElement);
    }
    
    // Signature required for some packages
    const signatureRequired = Math.random() < 0.3;
    const deliveryDelay = signatureRequired ? 4000 : 2500;
    
    setTimeout(() => {
      packageElement.classList.add('delivering');
      
      if (signatureRequired) {
        // Create signature indicator
        const signatureIndicator = document.createElement('div');
        signatureIndicator.className = 'signature-required';
        signatureIndicator.innerHTML = '✍️';
        signatureIndicator.style.left = targetHouse.x + 20 + 'px';
        signatureIndicator.style.top = targetHouse.y - 10 + 'px';
        
        aerialView.appendChild(signatureIndicator);
        
        setTimeout(() => {
          signatureIndicator.remove();
        }, 3000);
      }
      
      setTimeout(() => {
        // Package delivered
        createMotionPackageDeliveryBurst(targetHouse.x + 15, targetHouse.y + 15);
        targetHouse.element.classList.add('delivered');
        targetHouse.element.classList.add('package-delivered');
        targetHouse.delivered = true;
        setDeliveredCount(prev => prev + 1);
        
        // Place package at house
        createPackagePlacement(targetHouse);
        
        packageElement.remove();
        
        // Deactivate dolly
        if (usesDolly) {
          setDollyActive(false);
          const dolly = dollyRef.current;
          if (dolly) {
            dolly.classList.remove('active');
            setTimeout(() => dolly.style.display = 'none', 500);
          }
        }
        
      }, deliveryDelay);
    }, 1000);
  };

  const createPackagePlacement = (house) => {
    // Create a package box at the house
    const packageBox = document.createElement('div');
    packageBox.className = 'package-placement';
    packageBox.style.left = house.x + 25 + 'px';
    packageBox.style.top = house.y + 18 + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(packageBox);
      
      // Remove after some time (packages get picked up)
      setTimeout(() => {
        packageBox.classList.add('picked-up');
        setTimeout(() => packageBox.remove(), 1000);
      }, 15000);
    }
  };

  // Motion-enhanced package delivery burst with realistic physics
  const createMotionPackageDeliveryBurst = (x, y) => {
    const burst = document.createElement('div');
    burst.className = 'delivery-burst package-burst motion-enhanced';
    burst.style.left = (x - 30) + 'px';
    burst.style.top = (y - 30) + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(burst);
      
      // Create package-specific burst effect
      for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div');
        particle.className = 'package-burst-particle';
        const angle = (i / 8) * 2 * Math.PI;
        const distance = 20 + Math.random() * 15;
        const particleX = Math.cos(angle) * distance;
        const particleY = Math.sin(angle) * distance;
        
        burst.appendChild(particle);
        
        // Enhanced spring animation for package particles
        particle.animate([
          { 
            transform: 'translate(0, 0) scale(0) rotate(0deg)', 
            opacity: 1,
            backgroundColor: '#f59e0b'
          },
          { 
            transform: `translate(${particleX * 0.6}px, ${particleY * 0.6}px) scale(1.5) rotate(180deg)`, 
            opacity: 0.9,
            backgroundColor: '#f97316'
          },
          { 
            transform: `translate(${particleX * 1.2}px, ${particleY * 1.2}px) scale(0.3) rotate(360deg)`, 
            opacity: 0,
            backgroundColor: '#ea580c'
          }
        ], {
          duration: 1500 + Math.random() * 500,
          easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', // Bouncy spring
          fill: 'forwards'
        });
      }
      
      // Main burst animation with momentum
      burst.animate([
        { 
          transform: 'scale(0) rotate(0deg)', 
          opacity: 0,
          filter: 'blur(10px)'
        },
        { 
          transform: 'scale(1.3) rotate(90deg)', 
          opacity: 1,
          filter: 'blur(2px)'
        },
        { 
          transform: 'scale(1) rotate(180deg)', 
          opacity: 0.8,
          filter: 'blur(0px)'
        },
        { 
          transform: 'scale(0.5) rotate(270deg)', 
          opacity: 0,
          filter: 'blur(5px)'
        }
      ], {
        duration: 2200,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        fill: 'forwards'
      });
      
      setTimeout(() => burst.remove(), 2500);
    }
  };

  // Motion-enhanced package truck deactivation with smooth transitions
  const deactivatePackageTruck = async () => {
    const packageTruck = packageTruckRef.current;
    if (packageTruck) {
      packageTruck.classList.add('leaving');
      
      // Animate truck leaving with realistic physics
      await packageTruckControls.start({
        x: -120,
        y: packageTruckY.get(),
        scale: 0.9,
        opacity: 0.7,
        transition: {
          type: "spring",
          stiffness: 60,
          damping: 25,
          mass: 2.0,
          duration: 3
        }
      });
      
      // Reset state after animation
      setPackageTruckActive(false);
      setPackageQueue([]);
      packageTruckX.set(packageTruckPosition.x);
      packageTruckY.set(packageTruckPosition.y);
      
      // Reset visual state
      await packageTruckControls.set({
        x: packageTruckPosition.x,
        y: packageTruckPosition.y,
        scale: 1,
        opacity: 1
      });
      
      packageTruck.className = 'package-truck'; // Reset classes
      packageTruck.style.display = 'none';
    }
  };

  // Create delivery burst effect
  const createDeliveryBurst = (x, y) => {
    const burst = document.createElement('div');
    burst.className = 'delivery-burst';
    burst.style.left = (x - 15) + 'px';
    burst.style.top = (y - 15) + 'px';
    
    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(burst);
    }
    
    setTimeout(() => {
      burst.classList.add('active');
      setTimeout(() => burst.remove(), 1000);
    }, 50);
  };

  // Enhanced delivery animation with neighborhood-aware routing
  const deliverMail = () => {
    const undeliveredHouses = houses.filter(h => !h.delivered);
    
    if (undeliveredHouses.length === 0) {
      // Reset animation
      setTimeout(() => {
        setHouses(prevHouses => 
          prevHouses.map(h => {
            h.element.classList.remove('delivered');
            h.delivered = false;
            return h;
          })
        );
        setDeliveredCount(0);
        
        // Reset truck position responsively
        const containerHeight = aerialViewRef.current?.offsetHeight || 400;
        const isMobile = window.innerWidth <= 768;
        const newPosition = { 
          x: isMobile ? 30 : 50, 
          y: Math.min(isMobile ? 250 : 300, containerHeight - 50)
        };
        setCurrentTruckPosition(newPosition);
        
        const mailTruck = mailTruckRef.current;
        if (mailTruck) {
          mailTruck.style.left = newPosition.x + 'px';
          mailTruck.style.top = newPosition.y + 'px';
        }
      }, 3000);
      return;
    }
    
    // Prioritize deliveries by neighborhood for efficiency
    const neighborhoodGroups = {};
    undeliveredHouses.forEach(house => {
      if (!neighborhoodGroups[house.neighborhood]) {
        neighborhoodGroups[house.neighborhood] = [];
      }
      neighborhoodGroups[house.neighborhood].push(house);
    });
    
    // Pick a neighborhood and deliver 2-5 houses
    const neighborhoods = Object.keys(neighborhoodGroups);
    const targetNeighborhood = neighborhoods[Math.floor(Math.random() * neighborhoods.length)];
    const availableHouses = neighborhoodGroups[targetNeighborhood];
    
    const deliveryCount = Math.min(
      Math.floor(Math.random() * 2) + 1, // Deliver 1-2 houses at a time for clarity
      availableHouses.length
    );
    
    const targetHouses = [];
    for (let i = 0; i < deliveryCount; i++) {
      const randomIndex = Math.floor(Math.random() * availableHouses.length);
      const targetHouse = availableHouses.splice(randomIndex, 1)[0];
      targetHouse.delivered = true;
      
      // Remove from undelivered houses
      const mainIndex = undeliveredHouses.findIndex(h => h.id === targetHouse.id);
      if (mainIndex > -1) {
        undeliveredHouses.splice(mainIndex, 1);
      }
      
      targetHouses.push(targetHouse);
    }
    
    // Move truck to the delivery neighborhood
    moveTruckToArea(targetHouses);
    
    // Calculate animation duration for truck movement
    const distance = Math.sqrt(
      Math.pow(targetHouses[0].x - currentTruckPosition.x, 2) + 
      Math.pow(targetHouses[0].y - currentTruckPosition.y, 2)
    );
    const truckMovementTime = Math.min(Math.max(distance * 3, 800), 2000);
    
    // Deliver mail with staggered timing - wait for truck to arrive first
    targetHouses.forEach((house, index) => {
      setTimeout(() => {
        createMailDelivery(house);
      }, truckMovementTime + (index * 600) + 300); // Wait for truck + staggered delivery
    });
  };

  // Start advanced state machine animation system
  const startAdvancedAnimation = () => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }
    if (physicsIntervalRef.current) {
      clearInterval(physicsIntervalRef.current);
    }
    
    // State machine updates at 60 FPS for smooth animation
    physicsIntervalRef.current = setInterval(() => {
      updateTruckState();
    }, 16); // ~60 FPS
    
    // Periodic state evaluation for route planning
    animationIntervalRef.current = setInterval(() => {
      if (truckState === 'IDLE') {
        const undeliveredHouses = houses.filter(h => !h.delivered);
        if (undeliveredHouses.length > 0) {
          setTruckState('ROUTE_PLANNING');
        }
      }
    }, 1000);
  };

  // Legacy function for compatibility
  const startAnimation = () => {
    startAdvancedAnimation();
  };

  // Campaign button - redirect to auth
  const startCampaign = () => {
    navigate('/auth');
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setMobileMenuOpen(false);
  };

  // Add the missing function definitions and fix the animation system
  const initializeStreetGrid = () => {
    createStreetGrid();
  };

  // Enhanced animation system with proper function definitions
  const startHeroAnimation = () => {
    const aerialView = aerialViewRef.current;
    if (!aerialView) return;

    // Create dramatic entrance sequence
    const entranceSequence = [
      { delay: 500, action: () => createEntranceEffect() },
      { delay: 1000, action: () => createStreetGrid() },
      { delay: 1500, action: () => createNeighborhood() },
      { delay: 2000, action: () => initMailTruck() },
      { delay: 2500, action: () => startAdvancedAnimation() }
    ];

    entranceSequence.forEach(({ delay, action }) => {
      setTimeout(action, delay);
    });
  };

  // Enhanced entrance effect
  const createEntranceEffect = () => {
    const aerialView = aerialViewRef.current;
    if (!aerialView) return;

    // Create sweeping reveal animation
    const revealOverlay = document.createElement('div');
    revealOverlay.className = 'hero-reveal-overlay';
    revealOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(34, 197, 94, 0.1));
      z-index: 100;
      opacity: 0;
      animation: heroReveal 2s ease-out forwards;
    `;

    aerialView.appendChild(revealOverlay);

    // Add particle effects
    for (let i = 0; i < 8; i++) {
      setTimeout(() => createEntranceParticle(i), i * 200);
    }

    setTimeout(() => revealOverlay.remove(), 2000);
  };

  const createEntranceParticle = (index) => {
    const particle = document.createElement('div');
    particle.className = 'entrance-particle';
    particle.style.cssText = `
      position: absolute;
      width: 4px;
      height: 4px;
      background: rgba(34, 197, 94, 0.8);
      border-radius: 50%;
      top: ${20 + Math.random() * 60}%;
      left: ${10 + Math.random() * 80}%;
      animation: entranceSparkle 1.5s ease-out forwards;
      z-index: 101;
    `;

    const aerialView = aerialViewRef.current;
    if (aerialView) {
      aerialView.appendChild(particle);
      setTimeout(() => particle.remove(), 1500);
    }
  };

  // Initialize location only; disable legacy hero animation (replaced by new map component)
  useEffect(() => {
    detectUserLocation();
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
      if (physicsIntervalRef.current) {
        clearInterval(physicsIntervalRef.current);
      }
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }
    };
  }, []);

  // Add performance optimization constants
  const ANIMATION_CONFIG = {
    reducedMotion: false,
    mobileOptimizations: true,
    particleLimit: window.innerWidth <= 768 ? 15 : 30,
    frameRate: 60
  };

  // Check for reduced motion preference
  const checkReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // Optimized delivery animation
  const createOptimizedDelivery = (targetHouse) => {
    if (checkReducedMotion()) {
      // Simplified animation for accessibility
      targetHouse.element.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.8), rgba(16, 185, 129, 0.9))';
      targetHouse.element.style.transform = 'scale(1.05)';
      return;
    }

    // Use CSS animations instead of JavaScript for better performance
    targetHouse.element.classList.add('delivered');
    
    // Add subtle animation for mobile
    if (window.innerWidth <= 768) {
      targetHouse.element.style.animationDuration = '0.8s';
    }
  };

  // Throttled animation updates
  const throttledAnimation = (callback, delay = 16) => {
    let timeoutId;
    return () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        callback();
        timeoutId = null;
      }, delay);
    };
  };

  // Mobile-specific optimizations
  const optimizeForMobile = () => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // Reduce animation complexity
      document.documentElement.style.setProperty('--animation-duration', '0.8s');
      
      // Simplify particle effects
      const particles = document.querySelectorAll('.entrance-particle');
      particles.forEach(p => p.style.animationDuration = '1s');
    }
  };

  // Enhanced useEffect with optimizations
  useEffect(() => {
    // Initialize optimizations
    optimizeForMobile();
    
    // Handle window resize
    const handleResize = throttledAnimation(() => {
      optimizeForMobile();
    }, 250);
    
    window.addEventListener('resize', handleResize);
    
    // Start location detection immediately
    detectUserLocation();
    
    const timer = setTimeout(() => {
      // Use the new enhanced animation system
      startHeroAnimation();
    }, 100);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
      if (physicsIntervalRef.current) {
        clearInterval(physicsIntervalRef.current);
      }
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }
    };
  }, []);

  // Re-create neighborhood when boundary data is loaded
  useEffect(() => {
    if (!locationLoading && cityBoundary && houses.length === 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        createNeighborhood();
      }, 300);
    }
  }, [locationLoading, cityBoundary]);

  // Dynamic stats based on location
  const responseRate = Math.min(12.8 + (deliveredCount * 0.1), 15.2);
  const progress = (deliveredCount / totalHouses) * 100;
  
  // Get boundary center for better map positioning
  const mapCenter = cityBoundary ? getBoundaryCenter(cityBoundary) : { lat: 32.7157, lng: -117.1611 };

  return (
    <div className="home">
      <div className="bg-gradient"></div>
      
      <nav className="nav">
        <div className="nav-content">
          <div className="logo">
            <img src={logo} alt="San Diego Mailers" className="logo-image" />
          </div>
          <div className="nav-links">
            <button className="nav-link" onClick={() => scrollToSection('features')}>Features</button>
            <button className="nav-link" onClick={() => scrollToSection('scheduler')}>Schedule</button>
            <button className="nav-link" onClick={() => scrollToSection('templates')}>Templates</button>
            <button className="nav-link" onClick={() => scrollToSection('analytics')}>Analytics</button>
            <button className="nav-link" onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button className="cta-secondary" onClick={startCampaign}>Start Growing Practice</button>
          </div>
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <button className="nav-link" onClick={() => scrollToSection('features')}>Features</button>
            <button className="nav-link" onClick={() => scrollToSection('scheduler')}>Schedule</button>
            <button className="nav-link" onClick={() => scrollToSection('templates')}>Templates</button>
            <button className="nav-link" onClick={() => scrollToSection('analytics')}>Analytics</button>
            <button className="nav-link" onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button className="cta-secondary" onClick={startCampaign} style={{ marginTop: '12px' }}>Start Growing Practice</button>
          </div>
        </div>
      </nav>

      <div className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <div className="badge-dot"></div>
            Medical Marketing Specialists
          </div>
          <h1>Grow Your Medical Practice</h1>
          <p className="hero-subtitle">
            Get more patients with targeted direct mail campaigns designed specifically for medical offices, clinics, and healthcare practices in San Diego.
          </p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={startCampaign}>
              Get More Patients Now
            </button>
            <button className="cta-secondary" onClick={() => scrollToSection('pricing')}>See Pricing</button>
          </div>
        </div>

        <div className="stats-panel">
          <div className="stats-title">Medical Practice Growth</div>
          <div className="stat-item">
            <span className="stat-label">Mailers Delivered</span>
            <span className="stat-value">{deliveredCount} / {totalHouses}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Patient Open Rate</span>
            <span className="stat-value">94.2%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">New Patient Calls</span>
            <span className="stat-value">{responseRate.toFixed(1)}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Practice ROI</span>
            <span className="stat-value">287%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <div className="map-container">
          <SanDiegoMailDeliveryMap />
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Stop Losing Patients to Your Competition</h2>
            <p className="section-subtitle">While you're reading this, 3 potential patients just chose another practice. Here's how to finally fill your appointment book.</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">😰</div>
              <h3>"My appointment book is empty again"</h3>
              <div className="animated-visual">
                <div className="empty-schedule-animation">
                  <div className="schedule-header">
                    <div className="doctor-icon">👨‍⚕️</div>
                    <div className="schedule-title">Dr. Smith's Schedule</div>
                    <div className="week-indicator">This Week</div>
                  </div>
                  <div className="appointment-slots">
                    <div className="time-slot empty-slot">
                      <div className="time">9:00 AM</div>
                      <div className="patient-info">AVAILABLE</div>
                    </div>
                    <div className="time-slot filled-slot">
                      <div className="time">10:30 AM</div>
                      <div className="patient-info">👤 John D.</div>
                    </div>
                    <div className="time-slot empty-slot">
                      <div className="time">12:00 PM</div>
                      <div className="patient-info">AVAILABLE</div>
                    </div>
                    <div className="time-slot empty-slot">
                      <div className="time">2:00 PM</div>
                      <div className="patient-info">AVAILABLE</div>
                    </div>
                    <div className="time-slot filled-slot">
                      <div className="time">3:30 PM</div>
                      <div className="patient-info">👤 Sarah M.</div>
                    </div>
                    <div className="time-slot empty-slot">
                      <div className="time">5:00 PM</div>
                      <div className="patient-info">AVAILABLE</div>
                    </div>
                  </div>
                  <div className="schedule-stats">
                    <div className="stat-item">
                      <span className="stat-number">2</span>
                      <span className="stat-label">Booked</span>
                    </div>
                    <div className="stat-divider">|</div>
                    <div className="stat-item">
                      <span className="stat-number">4</span>
                      <span className="stat-label">Empty</span>
                    </div>
                    <div className="stat-divider">|</div>
                    <div className="stat-item revenue-loss">
                      <span className="stat-number">-$1,200</span>
                      <span className="stat-label">Lost Revenue</span>
                    </div>
                  </div>
                  <div className="worry-indicators">
                    <div className="worry-dot">😰</div>
                    <div className="worry-dot">😰</div>
                    <div className="worry-dot">😰</div>
                  </div>
                </div>
              </div>
              <p>Stop wondering where your next patient will come from. Our precision targeting reaches households with patients who actually need your services - right when they need them most.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💸</div>
              <h3>"My postcards look like every other marketing piece"</h3>
              <div className="animated-visual">
                <div className="money-waste-animation">
                  <div className="money-stack">
                    <div className="dollar-bill">Generic</div>
                    <div className="dollar-bill">Generic</div>
                    <div className="dollar-bill">Generic</div>
                    <div className="dollar-bill">Generic</div>
                  </div>
                  <div className="waste-arrow">↓</div>
                  <div className="trash-can">🗑️</div>
                  <div className="waste-text">All look the same</div>
                </div>
              </div>
              <p>Stop sending generic postcards that get ignored. Our technology-powered medical templates use data-driven design that speaks directly to patient concerns and actually gets read - because they look like they came from a caring doctor, not a marketing agency.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>"My flyers are generic and miss local events"</h3>
              <div className="animated-visual">
                <div className="local-events-animation">
                  <div className="calendar-icon">📅</div>
                  <div className="event-dates">
                    <div className="event-date">Valentine's Day</div>
                    <div className="event-date">Spring Health Fair</div>
                    <div className="event-date">Back to School</div>
                    <div className="event-date">Holiday Season</div>
                  </div>
                  <div className="missed-opportunity">❌</div>
                  <div className="generic-flyer">Generic Flyer</div>
                  <div className="missed-text">Missing Local Context</div>
                </div>
              </div>
              <p>Stop sending generic flyers that ignore local events and holidays. Our technology automatically identifies San Diego-specific events, seasonal health trends, and local holidays to create timely, relevant campaigns that actually connect with your community.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">😤</div>
              <h3>"Big hospital systems are stealing my patients"</h3>
              <div className="animated-visual">
                <div className="competition-animation">
                  <div className="hospital-building">🏥</div>
                  <div className="patient-flow">
                    <div className="patient-dot">👤</div>
                    <div className="patient-dot">👤</div>
                    <div className="patient-dot">👤</div>
                  </div>
                  <div className="arrow">→</div>
                  <div className="small-practice">🏠</div>
                  <div className="competition-text">Patients going to big hospitals</div>
                </div>
              </div>
              <p>Stop losing patients to Scripps and Sharp. Our hyper-local targeting reaches patients in your exact neighborhood who want personal care, not corporate medicine.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📉</div>
              <h3>"I can't tell if my marketing is actually working"</h3>
              <div className="animated-visual">
                <div className="roi-transformation-animation">
                  <div className="money-input">
                    <div className="dollar-in">$1</div>
                    <div className="input-arrow">↓</div>
                  </div>
                  <div className="roi-multiplier">
                    <div className="multiply-symbol">📊</div>
                    <div className="multiplier">Track</div>
                  </div>
                  <div className="money-output">
                    <div className="output-arrow">↓</div>
                    <div className="dollar-out">Clear ROI</div>
                  </div>
                  <div className="roi-text">Smart Analytics</div>
                </div>
              </div>
              <p>Stop guessing about your marketing ROI. Our technology-based mailers include advanced tracking and analytics that show you exactly which campaigns bring in patients and how much revenue they generate.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>"I'm flying blind with my practice growth"</h3>
              <div className="animated-visual">
                <div className="growth-prediction-animation">
                  <div className="growth-chart">
                    <div className="chart-line">
                      <div className="data-point" style={{left: '10%', bottom: '20%'}}></div>
                      <div className="data-point" style={{left: '30%', bottom: '35%'}}></div>
                      <div className="data-point" style={{left: '50%', bottom: '25%'}}></div>
                      <div className="data-point" style={{left: '70%', bottom: '60%'}}></div>
                      <div className="data-point" style={{left: '90%', bottom: '80%'}}></div>
                    </div>
                    <div className="prediction-line">
                      <div className="prediction-dot" style={{left: '90%', bottom: '80%'}}></div>
                      <div className="prediction-dot" style={{left: '95%', bottom: '90%'}}></div>
                      <div className="prediction-dot" style={{left: '100%', bottom: '95%'}}></div>
                    </div>
                  </div>
                  <div className="growth-text">Data-Driven Growth</div>
                </div>
              </div>
              <p>Stop guessing about your practice growth. Our advanced analytics give you real-time insights into patient acquisition trends, campaign performance, and revenue patterns so you can make informed decisions about scaling your practice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Scheduler Section */}
      <section id="scheduler" className="scheduler-section">
        <div className="container">
          <div className="scheduler-content">
            <div className="scheduler-info">
              <h2>Schedule your patient acquisition campaign</h2>
              <p>Our medical marketing system finds optimal delivery dates to reach potential patients when they're most likely to book appointments.</p>
              <div className="scheduler-features">
                <div className="scheduler-feature">
                  <span className="checkmark">✓</span>
                  <span>Best times to reach patients</span>
                </div>
                <div className="scheduler-feature">
                  <span className="checkmark">✓</span>
                  <span>Healthcare seasonal trends</span>
                </div>
                <div className="scheduler-feature">
                  <span className="checkmark">✓</span>
                  <span>Avoid medical holidays</span>
                </div>
                <div className="scheduler-feature">
                  <span className="checkmark">✓</span>
                  <span>Follow-up appointment reminders</span>
                </div>
              </div>
              <button className="cta-primary" onClick={startCampaign}>Start Getting Patients</button>
            </div>
            <div className="scheduler-visual">
              <IndustrySelector
                selectedIndustry={selectedIndustry}
                onIndustryChange={setSelectedIndustry}
              />
              <CalendarWithTemplates selectedIndustry={selectedIndustry} />
            </div>
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section id="templates" className="templates-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Medical practice templates</h2>
            <p className="section-subtitle">Professional healthcare templates designed to attract patients and build trust</p>
          </div>
          <div className="templates-preview">
            <div className="template-card active">
              <div className="template-image">
                <div className="template-mockup business">
                  <div className="mockup-header">
                    <div className="mockup-logo"></div>
                    <div className="mockup-text-line"></div>
                  </div>
                  <div className="mockup-content">
                    <div className="mockup-title"></div>
                    <div className="mockup-text"></div>
                    <div className="mockup-cta"></div>
                  </div>
                </div>
              </div>
              <h4>General Practice</h4>
              <p>Professional design perfect for family medicine and primary care physicians</p>
            </div>
            <div className="template-card">
              <div className="template-image">
                <div className="template-mockup retail">
                  <div className="mockup-header retail-header">
                    <div className="mockup-offer">50% OFF</div>
                  </div>
                  <div className="mockup-content">
                    <div className="mockup-title"></div>
                    <div className="mockup-text"></div>
                    <div className="mockup-cta retail-cta"></div>
                  </div>
                </div>
              </div>
              <h4>Dental Practice</h4>
              <p>Attractive designs specifically for dental offices and orthodontists</p>
            </div>
            <div className="template-card">
              <div className="template-image">
                <div className="template-mockup real-estate">
                  <div className="mockup-header">
                    <div className="mockup-image-placeholder"></div>
                  </div>
                  <div className="mockup-content">
                    <div className="mockup-title"></div>
                    <div className="mockup-text"></div>
                    <div className="mockup-cta"></div>
                  </div>
                </div>
              </div>
              <h4>Specialist Practices</h4>
              <p>Tailored designs for cardiology, dermatology, and other medical specialties</p>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section id="analytics" className="analytics-section">
        <div className="container">
          <div className="analytics-content">
            <div className="analytics-info">
              <h2>Track your patient acquisition</h2>
              <p>Monitor new patient bookings, appointment calls, and practice revenue growth with detailed medical marketing analytics.</p>
              <div className="analytics-features">
                <div className="analytics-metric">
                  <div className="metric-number">94.2%</div>
                  <div className="metric-label">Patient Reach Rate</div>
                </div>
                <div className="analytics-metric">
                  <div className="metric-number">13.7%</div>
                  <div className="metric-label">Appointment Booking Rate</div>
                </div>
                <div className="analytics-metric">
                  <div className="metric-number">287%</div>
                  <div className="metric-label">Practice Revenue ROI</div>
                </div>
              </div>
            </div>
            <div className="analytics-visual">
              <div className="analytics-dashboard">
                <div className="dashboard-header">
                  <h4>Patient Acquisition Performance</h4>
                  <span className="time-range">Last 30 days</span>
                </div>
                <div className="chart-container">
                  <div className="chart-bars">
                    <div className="chart-bar" style={{ height: '60%' }}>
                      <div className="bar-fill sent"></div>
                      <span className="bar-label">Sent</span>
                    </div>
                    <div className="chart-bar" style={{ height: '85%' }}>
                      <div className="bar-fill delivered"></div>
                      <span className="bar-label">Delivered</span>
                    </div>
                    <div className="chart-bar" style={{ height: '40%' }}>
                      <div className="bar-fill opened"></div>
                      <span className="bar-label">Opened</span>
                    </div>
                    <div className="chart-bar" style={{ height: '25%' }}>
                      <div className="bar-fill responded"></div>
                      <span className="bar-label">Booked Appt</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Medical practice pricing</h2>
            <p className="section-subtitle">Affordable patient acquisition plans designed for medical practices</p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Starter</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">2.13</span>
                <span className="period">/piece</span>
              </div>
              <ul className="features-list">
                <li>Up to 1,000 pieces</li>
                <li>Basic templates</li>
                <li>Standard delivery</li>
                <li>Email support</li>
                <li>Basic analytics</li>
              </ul>
              <button className="pricing-btn" onClick={startCampaign}>Start Attracting Patients</button>
            </div>
            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <h3>Professional</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">1.69</span>
                <span className="period">/piece</span>
              </div>
              <ul className="features-list">
                <li>Up to 10,000 pieces</li>
                <li>Premium templates</li>
                <li>Priority delivery</li>
                <li>Phone + Email support</li>
                <li>Advanced analytics</li>
                <li>A/B testing</li>
              </ul>
              <button className="pricing-btn featured" onClick={startCampaign}>Start Free Trial</button>
            </div>
            <div className="pricing-card">
              <h3>Multi Practice</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">0.99</span>
                <span className="period">/piece</span>
              </div>
              <ul className="features-list">
                <li>Unlimited pieces</li>
                <li>Custom templates</li>
                <li>Express delivery</li>
                <li>Dedicated support</li>
                <li>Full analytics suite</li>
                <li>API access</li>
                <li>White-label options</li>
              </ul>
              <button className="pricing-btn" onClick={startCampaign}>Grow Your Practice</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;